import * as React from "react"
import { useEffect, useState, useCallback } from "react"
import {
    Alert,
    AlertTitle,
    Skeleton,
    Collapse,
    Box,
    Typography,
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableCell,
    TableRow,
} from "@mui/material"
import Grid from "@mui/material/Grid2"
import EventBusyIcon from "@mui/icons-material/EventBusy"

import PropertySection from "./PropertySection"


// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const FREQUENCY_LABELS = {
    "1-month": "Mensuel",
    "3-month": "Trimestriel",
    "6-month": "Semestriel",
    "1-year":  "Annuel",
}

function formatDate(dateStr) {
    if (!dateStr) return "—"
    return new Date(dateStr).toLocaleDateString("fr-FR", {
        day: "numeric", month: "long", year: "numeric",
    })
}

function formatAmount(value) {
    return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(value)
}

function formatCategory(cat) {
    if (!cat) return "—"
    return cat.charAt(0).toUpperCase() + cat.slice(1)
}


// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function SectionLabel({ children }) {
    return (
        <Typography
            variant="overline"
            color="text.secondary"
            sx={{ display: "block", mb: 1, letterSpacing: "0.08em" }}
        >
            {children}
        </Typography>
    )
}

function LeaseMetaGrid({ lease, tenantNames }) {
    const items = [
        { label: "Locataire(s)", value: tenantNames.length > 0 ? tenantNames.join(", ") : "—" },
        { label: "Début",        value: formatDate(lease.start_date) },
        { label: "Fin",          value: lease.end_date ? formatDate(lease.end_date) : "En cours" },
        { label: "Type",         value: lease.furnished ? "Meublé" : "Non meublé" },
        { label: "Fréquence",    value: FREQUENCY_LABELS[lease.payment_frequency] ?? lease.payment_frequency },
        { label: "Indice",       value: lease.reference_price_index_name },
    ]

    return (
        <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 3, rowGap: 0.75, mt: 0.5 }}>
            {items.map(({ label, value }) => (
                <React.Fragment key={label}>
                    <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center" }}>
                        {label}
                    </Typography>
                    <Typography variant="body2" sx={{ alignSelf: "center" }}>
                        {value}
                    </Typography>
                </React.Fragment>
            ))}
        </Box>
    )
}


// ---------------------------------------------------------------------------
// PropertyCurrentCard — "Bail & loyer actuels" section of the Location tab.
//
// DECISION: tenants now live here (moved from PropertyInfoCard) — a tenant
// is tied to the lease, and this is where the rest of the lease lives.
//
// DECISION: laid out as two columns (lease facts / rent breakdown) rather
// than stacked. The old single-column layout was a side effect of being
// squeezed into a 5-of-12 page column; freed from that constraint inside
// the Location tab, the two sit naturally side by side and the section
// takes noticeably less vertical space.
//
// Props
//   supabase    – initialised Supabase client
//   propertyId  – UUID of the property to display
// ---------------------------------------------------------------------------
export default function PropertyCurrentCard({ supabase, propertyId }) {

    const [lease,   setLease]   = useState(null)
    const [tenants, setTenants] = useState([])
    const [buckets, setBuckets] = useState([])
    const [errors,  setErrors]  = useState({})
    const [loading, setLoading] = useState(true)


    // ── Fetch ────────────────────────────────────────────────────────────────

    const fetchAll = useCallback(async () => {
        const today = new Date().toISOString().split("T")[0]
        setLoading(true)
        setErrors({})

        // ── 1. Active lease ──────────────────────────────────────────────────
        const { data: leaseData, error: leaseError } = await supabase
            .from("lease")
            .select("id, start_date, end_date, furnished, payment_frequency, reference_price_index_name")
            .eq("property_id", propertyId)
            .or(`end_date.is.null,end_date.gte.${today}`)
            .order("start_date", { ascending: false })
            .limit(1)
            .maybeSingle()

        if (leaseError) {
            setErrors(prev => ({ ...prev, lease: `Impossible de charger le bail : ${leaseError.message}` }))
            setLoading(false)
            return
        }
        setLease(leaseData)

        if (!leaseData) {
            setTenants([])
            setBuckets([])
            setLoading(false)
            return
        }

        // ── 2. Tenants + active buckets in parallel ──────────────────────────
        //
        // DECISION: filter valid_until server-side — we only need active
        // amounts here. The expired-bucket history is handled by
        // PropertyHistoryCard.
        const [tenantsRes, bucketsRes] = await Promise.all([
            supabase
                .from("tenant")
                .select(`
                    person:person_id ( title, first_name, last_name ),
                    company:company_id ( name )
                `)
                .eq("lease_id", leaseData.id),
            supabase
                .from("transactions_bucket")
                .select("id, category, default_value, valid_until")
                .eq("lease_id", leaseData.id)
                .or(`valid_until.is.null,valid_until.gte.${today}`)
                .order("default_value", { ascending: false }),
        ])

        if (tenantsRes.error) {
            setErrors(prev => ({ ...prev, tenants: `Impossible de charger les locataires : ${tenantsRes.error.message}` }))
        } else {
            setTenants((tenantsRes.data ?? []).map(t => t.person
                ? `${t.person.title} ${t.person.first_name} ${t.person.last_name}`
                : (t.company?.name ?? "Inconnu")))
        }

        if (bucketsRes.error) {
            setErrors(prev => ({ ...prev, buckets: `Impossible de charger les montants : ${bucketsRes.error.message}` }))
        } else {
            setBuckets(bucketsRes.data ?? [])
        }

        setLoading(false)
    }, [supabase, propertyId])


    // ── Initial load ─────────────────────────────────────────────────────────
    useEffect(() => {
        if (!propertyId) return
        fetchAll()
    }, [propertyId, fetchAll])


    // ── Realtime ─────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!propertyId) return

        const channel = supabase
            .channel(`property-current-card-${propertyId}`)
            .on("postgres_changes",
                { event: "*", schema: "public", table: "lease", filter: `property_id=eq.${propertyId}` },
                fetchAll)
            .on("postgres_changes",
                { event: "*", schema: "public", table: "transactions_bucket" },
                fetchAll)
            .on("postgres_changes",
                { event: "*", schema: "public", table: "tenant" },
                fetchAll)
            .subscribe()

        return () => { supabase.removeChannel(channel) }
    }, [supabase, propertyId, fetchAll])


    // ── Derived ──────────────────────────────────────────────────────────────
    const hasErrors      = Object.keys(errors).length > 0
    const totalPerPeriod = buckets.reduce((sum, b) => sum + (b.default_value ?? 0), 0)


    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <PropertySection title="Bail & loyer actuels">

            {/* ── Error banners ── */}
            <Collapse in={hasErrors}>
                {Object.entries(errors).map(([key, message]) => (
                    <Alert severity="error" key={key} sx={{ mb: 1 }}>
                        <AlertTitle>Erreur de chargement</AlertTitle>
                        {message}
                    </Alert>
                ))}
            </Collapse>

            {loading ? (
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 5 }}>
                        <Skeleton variant="text" width="40%" sx={{ fontSize: "0.7rem" }} />
                        <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 1, mt: 1 }} />
                    </Grid>
                    <Grid size={{ xs: 12, md: 7 }}>
                        <Skeleton variant="text" width="40%" sx={{ fontSize: "0.7rem" }} />
                        <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 1, mt: 1 }} />
                    </Grid>
                </Grid>
            ) : !lease ? (

                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 4, gap: 1 }}>
                    <EventBusyIcon sx={{ fontSize: 40, color: "text.disabled" }} />
                    <Typography variant="body2" color="text.secondary">
                        Aucun bail actif pour ce bien.
                    </Typography>
                </Box>

            ) : (
                <Grid container spacing={3}>

                    {/* ── Column 1 : Bail actif ── */}
                    <Grid size={{ xs: 12, md: 5 }}>
                        <SectionLabel>Bail actif</SectionLabel>
                        <Box sx={{
                            border: "1px solid", borderColor: "divider",
                            borderRadius: 1, p: 1.5, bgcolor: "action.hover",
                        }}>
                            <LeaseMetaGrid lease={lease} tenantNames={tenants} />
                        </Box>
                    </Grid>

                    {/* ── Column 2 : Loyers & Charges actuels ── */}
                    <Grid size={{ xs: 12, md: 7 }}>
                        <SectionLabel>Loyers & Charges actuels</SectionLabel>
                        {buckets.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                Aucun montant enregistré.
                            </Typography>
                        ) : (
                            <TableContainer sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: "action.hover" }}>
                                            <TableCell sx={{ fontSize: "0.7rem", fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                                                Poste
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontSize: "0.7rem", fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                                                Montant
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {buckets.map(b => (
                                            <TableRow key={b.id} sx={{ "&:last-child td": { borderBottom: 0 } }}>
                                                <TableCell sx={{ color: "text.secondary" }}>
                                                    {formatCategory(b.category)}
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                                                    {formatAmount(b.default_value)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        <TableRow sx={{ bgcolor: "action.hover" }}>
                                            <TableCell sx={{ fontWeight: 700, borderBottom: 0 }}>
                                                Total
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums", borderBottom: 0 }}>
                                                {formatAmount(totalPerPeriod)}
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Grid>

                </Grid>
            )}
        </PropertySection>
    )
}